<template>
  <div class="space-y-2">
    <div class="text-sm">
      <!-- Result Section -->
      <StatusBanner v-if="resultData?.createdUserId" variant="success" class="mb-3">
        <span class="font-medium">{{ t('userCreated') }}:</span>
        <router-link
          :to="{ name: 'user', params: { id: resultData.createdUserId } }"
          class="underline"
        >
          {{ t('viewUserProfile') }}
        </router-link>
      </StatusBanner>

      <!-- User Link Section -->
      <StatusBanner v-if="jobData.userId" variant="info" class="mb-3">
        <span class="font-medium">{{ t('existingUser') }}:</span>
        <router-link :to="{ name: 'user', params: { id: jobData.userId } }" class="underline">
          {{ t('viewUserProfile') }}
        </router-link>
      </StatusBanner>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <strong>{{ t('firstName') }}:</strong> {{ userData.firstName }}
        </div>
        <div>
          <strong>{{ t('lastName') }}:</strong> {{ userData.lastName }}
        </div>
        <div>
          <strong>{{ t('email') }}:</strong> {{ userData.email }}
        </div>
        <div v-if="userData.role">
          <strong>{{ t('role') }}:</strong> {{ userData.role }}
        </div>
        <div v-if="partnerTypeDisplay.display !== '-' || partnerTypeDisplay.struckThrough">
          <strong>{{ t('partnerType') }}:</strong>
          <span
            v-if="partnerTypeDisplay.struckThrough"
            class="mr-1 text-obsidian-400 line-through"
            >{{ partnerTypeDisplay.struckThrough }}</span
          >
          {{ partnerTypeDisplay.display }}
        </div>
        <div v-if="userData.phone">
          <strong>{{ t('phone') }}:</strong> {{ userData.phone }}
        </div>
        <div v-if="userData.company">
          <strong>{{ t('company') }}:</strong> {{ userData.company }}
        </div>
        <div v-if="userData.street">
          <strong>{{ t('street') }}:</strong> {{ userData.street }}
        </div>
        <div v-if="userData.city">
          <strong>{{ t('city') }}:</strong> {{ userData.city }}
        </div>
        <div v-if="userData.zip">
          <strong>{{ t('zip') }}:</strong> {{ userData.zip }}
        </div>
        <div v-if="userData.country">
          <strong>{{ t('country') }}:</strong> {{ userData.country }}
        </div>
      </div>

      <div v-if="userData.stationIds && userData.stationIds.length > 0" class="mt-2">
        <strong>{{ t('assignedStations') }}:</strong>
        <ul class="ml-4 list-disc">
          <li v-for="(stationName, index) in stationNames" :key="userData.stationIds[index]">
            {{ stationName }}
          </li>
        </ul>
      </div>

      <div v-if="userData.role === 'AGENCY' || userData.role === 'COMPANY'" class="mt-4">
        <div class="mt-2 grid grid-cols-2 gap-4">
          <div v-if="isCommissionAgency">
            <strong>{{ t('commissionPercent') }}:</strong>
            <div>
              <span
                v-if="commissionDisplay.struckThrough"
                class="mr-1 text-obsidian-400 line-through"
                >{{ commissionDisplay.struckThrough }}</span
              >
              {{ commissionDisplay.display }}
            </div>
          </div>
          <div v-if="userData.role === 'COMPANY'">
            <strong>{{ t('discountPercent') }}:</strong>
            <div>
              <span
                v-if="discountDisplay.struckThrough"
                class="mr-1 text-obsidian-400 line-through"
                >{{ discountDisplay.struckThrough }}</span
              >
              {{ discountDisplay.display }}
            </div>
          </div>
          <div v-if="userData.role === 'COMPANY'">
            <strong>{{ t('noDeposit') }}:</strong>
            <span
              v-if="noDepositDisplay.struckThrough"
              class="mr-1 text-obsidian-400 line-through"
              >{{ noDepositDisplay.struckThrough }}</span
            >
            {{ noDepositDisplay.display }}
          </div>
          <div v-if="userData.role === 'COMPANY' && userData.agencySettings?.acquisitionSource">
            <strong>{{ t('acquisitionSource') }}:</strong>
            {{ t(`acq.${userData.agencySettings.acquisitionSource}`) }}
          </div>
          <div
            v-if="userData.role === 'COMPANY' && userData.agencySettings?.intendedRentalFrequency"
          >
            <strong>{{ t('intendedRentalFrequency') }}:</strong>
            {{ t(`freq.${userData.agencySettings.intendedRentalFrequency}`) }}
          </div>
          <div
            v-if="
              userData.role === 'COMPANY' && userData.agencySettings?.requiredVehicleClasses?.length
            "
          >
            <strong>{{ t('requiredVehicleClasses') }}:</strong>
            <ul class="ml-4 list-disc">
              <li v-for="vc in userData.agencySettings.requiredVehicleClasses" :key="vc">
                {{ t(`vclass.${vc}`) }}
              </li>
            </ul>
          </div>
          <div v-if="userData.agencySettings?.companyInvoicingType">
            <strong>{{ t('companyInvoicingTypeLabel') }}:</strong>
            {{ t(`companyInvoicingType.${userData.agencySettings.companyInvoicingType}`) }}
          </div>
          <div v-if="userData.agencySettings?.isAgency !== undefined">
            <strong>{{ t('isAgency') }}:</strong>
            {{ userData.agencySettings.isAgency ? t('yes') : t('no') }}
          </div>
          <div v-if="userData.agencySettings?.invoiceEmail">
            <strong>{{ t('invoiceEmail') }}:</strong>
            {{ userData.agencySettings.invoiceEmail }}
          </div>
          <div v-if="userData.agencySettings?.contactPerson">
            <strong>{{ t('contactPerson') }}:</strong>
            {{ userData.agencySettings.contactPerson }}
          </div>

          <div
            v-if="
              userData.role === 'COMPANY' && userData.agencySettings?.allowedEmployeeDomains?.length
            "
          >
            <strong>{{ t('allowedEmployeeEmailsOrDomains') }}:</strong>
            <ul class="ml-4 list-disc">
              <li v-for="item in userData.agencySettings?.allowedEmployeeDomains || []" :key="item">
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="userData.comment" class="mt-4">
        <strong>{{ userData.role === 'COMPANY' ? t('commentDetailed') : t('comment') }}:</strong>
        <div class="whitespace-pre-line">{{ userData.comment }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings';
import { usePartnerSubTypes, useUser } from '@/queries/use-users';
import { useStationsWithQuery } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import StatusBanner from '@/components/StatusBanner.vue';
import { AgencyType } from '@/entities/auth/agency-type.enum';
import { formatPercentOption, resolveOverriddenValue } from './resolve-overridden-value';

const props = defineProps<{
  jobData: Record<string, any>;
  resultData?: Record<string, any>;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const { data: yieldSettings } = useYieldManagementSettings();
const { data: partnerSubTypes } = usePartnerSubTypes();

const existingUserId = computed(() => props.jobData.userId as string | undefined);
const { data: existingUser } = useUser(
  computed(() => existingUserId.value ?? ''),
  Boolean(existingUserId.value),
);

const partnerSubType = computed(() => {
  const partnerSubTypeId = props.jobData.userData.partnerSubTypeId;
  if (!partnerSubTypeId) return undefined;
  return (partnerSubTypes.value ?? []).find((subType) => subType.id === partnerSubTypeId);
});

const partnerSubTypeName = computed(() =>
  partnerSubType.value ? getLocalizedField(partnerSubType.value.name) : null,
);

const userData = computed(() => props.jobData.userData ?? {});

const previousPartnerSubTypeName = computed(() => {
  const previous = existingUser.value?.partnerSubType;
  return previous ? getLocalizedField(previous.name) : null;
});

const partnerTypeDisplay = computed(() =>
  resolveOverriddenValue(
    previousPartnerSubTypeName.value,
    partnerSubTypeName.value,
    (name) => name,
  ),
);

const isCommissionAgency = computed(() => {
  const type = partnerSubType.value?.type ?? userData.value.agencyType;
  return userData.value.role === 'AGENCY' && type === AgencyType.AGENCY_ON_COMMISSION_BASIS;
});

// Fetch stations if there are station IDs
const stationIds = computed(() => userData.value.stationIds || []);
const { data: stationsData } = useStationsWithQuery(
  computed(() => ({
    stationIds: stationIds.value,
    limit: 500,
  })),
  computed(() => stationIds.value.length > 0),
);
const stations = useFlattenPaginatedData(stationsData);

// Compute station names list
const stationNames = computed(() => {
  if (!stations.value) return [];
  return stationIds.value.map((stationId: string) => {
    const station = stations.value.find((s) => s.id === stationId);
    return station ? getLocalizedField(station.info) : stationId;
  });
});

const commissionDisplay = computed(() =>
  resolveOverriddenValue(
    partnerSubType.value?.agencySettings.commissionPercent,
    userData.value.agencySettings?.commissionPercent,
    (value) => formatPercentOption(value, yieldSettings.value?.commissionPercentageOptions),
  ),
);

const discountDisplay = computed(() =>
  resolveOverriddenValue(
    partnerSubType.value?.agencySettings.discountPercent,
    userData.value.agencySettings?.discountPercent,
    (value) => formatPercentOption(value, yieldSettings.value?.discountPercentageOptions),
  ),
);

const noDepositDisplay = computed(() =>
  resolveOverriddenValue(
    partnerSubType.value?.agencySettings.noDeposit,
    userData.value.agencySettings?.noDeposit,
    (value) => (value ? t('yes') : t('no')),
  ),
);
</script>

<i18n lang="json">
{
  "en": {
    "firstName": "First Name",
    "lastName": "Last Name",
    "email": "Email",
    "role": "Role",
    "partnerType": "Partner Type",
    "phone": "Phone",
    "company": "Company",
    "street": "Street",
    "city": "City",
    "zip": "ZIP Code",
    "country": "Country",
    "assignedStations": "Assigned Stations",
    "existingUser": "Existing User",
    "viewUserProfile": "View User Profile",
    "userCreated": "User Created",
    "agencySettings": "Agent Settings",
    "commissionPercent": "Commission Percent",
    "discountPercent": "Discount Percent",
    "invoiceEmail": "Invoice Email",
    "contactPerson": "Contact Person",
    "allowedEmployeeEmailsOrDomains": "Allowed Employee Emails or Domains",
    "noDeposit": "No Deposit",
    "yes": "Yes",
    "no": "No",
    "companyInvoicingTypeLabel": "Company Invoicing Type",
    "isAgency": "Is Agent",
    "comment": "Comment",
    "commentDetailed": "Comment (How did you win this customer? Short story)",
    "acquisitionSource": "Where does the customer come from?",
    "intendedRentalFrequency": "How often will they rent?",
    "requiredVehicleClasses": "Which vehicle classes are needed?"
  },
  "de": {
    "firstName": "Vorname",
    "lastName": "Nachname",
    "email": "E-Mail",
    "role": "Rolle",
    "partnerType": "Partnertyp",
    "phone": "Telefon",
    "company": "Firma",
    "street": "Straße",
    "city": "Stadt",
    "zip": "PLZ",
    "country": "Land",
    "assignedStations": "Zugewiesene Stationen",
    "existingUser": "Bestehender Benutzer",
    "viewUserProfile": "Benutzerprofil anzeigen",
    "userCreated": "Benutzer erstellt",
    "agencySettings": "Agent-Einstellungen",
    "commissionPercent": "Provisionsprozentsatz",
    "discountPercent": "Rabattprozentsatz",
    "invoiceEmail": "Rechnungs-E-Mail",
    "contactPerson": "Ansprechpartner",
    "allowedEmployeeEmailsOrDomains": "Erlaubte Mitarbeiter-E-Mails oder Domains",
    "noDeposit": "Keine Kaution",
    "yes": "Ja",
    "no": "Nein",
    "companyInvoicingTypeLabel": "Firmen-Abrechnungstyp",
    "isAgency": "Ist Agent",
    "comment": "Kommentar",
    "commentDetailed": "Kommentar (Bitte beschreibe, wie Du den Kunden gewonnen hast und beschreibe die Geschichte kurz.)",
    "acquisitionSource": "Woher kommt der Kunde?",
    "intendedRentalFrequency": "Wie oft hat der Kunde vor zu mieten?",
    "requiredVehicleClasses": "Welche Fahrzeugklassen werden benötigt?"
  },
  "es": {
    "firstName": "Nombre",
    "lastName": "Apellidos",
    "email": "Correo electrónico",
    "role": "Rol",
    "partnerType": "Tipo de partner",
    "phone": "Teléfono",
    "company": "Empresa",
    "street": "Calle",
    "city": "Ciudad",
    "zip": "Código postal",
    "country": "País",
    "assignedStations": "Estaciones asignadas",
    "existingUser": "Usuario existente",
    "viewUserProfile": "Ver perfil de usuario",
    "userCreated": "Usuario creado",
    "agencySettings": "Ajustes de agencia",
    "commissionPercent": "Porcentaje de comisión",
    "discountPercent": "Porcentaje de descuento",
    "invoiceEmail": "Correo para facturas",
    "contactPerson": "Persona de contacto",
    "allowedEmployeeEmailsOrDomains": "Correos o dominios de empleados permitidos",
    "noDeposit": "Sin depósito",
    "yes": "Sí",
    "no": "No",
    "companyInvoicingTypeLabel": "Tipo de facturación de empresa",
    "isAgency": "Es agente",
    "comment": "Comentario",
    "commentDetailed": "Comentario (¿cómo consiguió a este cliente? Breve relato)",
    "acquisitionSource": "¿De dónde procede el cliente?",
    "intendedRentalFrequency": "¿Con qué frecuencia alquilará?",
    "requiredVehicleClasses": "¿Qué categorías de vehículo necesita?"
  }
}
</i18n>
